import { CheckCircle2, CircleDashed, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatDuration, shortTime, spanText } from "./alertText";
import type { LinkIncident, LinkIncidentStep } from "@/shared/api/models";

/**
 * 현장이 올린 통신 끊김 점검·조치 보고 한 건 (2026-09-24 지시 — 「문제점과 조치 결과 보고」).
 * 원인·조치 결과를 먼저 한 줄로, 그 아래 점검·조치 단계를 시각순으로. 글자는 자르지 않는다.
 */
export function IncidentReport({ inc, compact = false }: { inc: LinkIncident; compact?: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-[12px] border border-[var(--border)] px-3 py-2.5">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-[13px] font-semibold">
          {inc.reporter === "sensor" ? t("widgets.alerts.v2.incident.bySensor") : t("widgets.alerts.v2.incident.byManager")}
        </span>
        <span className="text-[12px] tabular-nums text-[var(--fg-subtle)]">
          {spanText(inc.started_at, inc.ended_at)} · {formatDuration(t, inc.duration_s)}
        </span>
      </div>
      <dl className="mt-1.5 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-[12.5px]">
        <dt className="text-[var(--fg-subtle)]">{t("widgets.alerts.v2.incident.cause")}</dt>
        <dd className="font-medium break-keep">{inc.cause || "—"}</dd>
        <dt className="text-[var(--fg-subtle)]">{t("widgets.alerts.v2.incident.result")}</dt>
        <dd className="font-medium break-keep">{inc.resolved_by || "—"}</dd>
      </dl>
      {!compact && (inc.actions.length > 0 || inc.checks.length > 0) && (
        <ol className="mt-2 space-y-1">
          {[...inc.checks.map((s) => ({ ...s, kind: "check" as const })), ...inc.actions.map((s) => ({ ...s, kind: "action" as const }))]
            .sort((a, b) => a.at.localeCompare(b.at))
            .slice(0, 30)
            .map((s, i) => <StepRow key={i} s={s} action={s.kind === "action"} />)}
        </ol>
      )}
    </div>
  );
}

function StepRow({ s, action }: { s: LinkIncidentStep; action: boolean }) {
  const Icon = s.ok === true ? CheckCircle2 : s.ok === false ? XCircle : CircleDashed;
  const color = s.ok === true ? "var(--success)" : s.ok === false ? "var(--danger)" : "var(--fg-subtle)";
  return (
    <li className="flex items-start gap-2 text-[12px] leading-snug">
      <span className="w-[38px] shrink-0 tabular-nums text-[var(--fg-subtle)]">{shortTime(s.at, false)}</span>
      <Icon size={13} aria-hidden className="mt-[2px] shrink-0" style={{ color }} />
      <span className="min-w-0 flex-1 break-keep [overflow-wrap:anywhere]">
        <span className={action ? "font-semibold" : "font-medium"}>{s.label || s.name}</span>
        {s.detail && <span className="text-[var(--fg-subtle)]"> · {s.detail}</span>}
      </span>
    </li>
  );
}
